import { useState } from "react";
import { Button, Tooltip, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";

export const ExcelExportButton = ({ filteredFiles, disabled, permissionExport }) => {
    const [exporting, setExporting] = useState(false);
    const getProjectName = (fullPath) => {
        if (!fullPath) return 'No especificado';
        const pathParts = fullPath.split('/');
        if (pathParts.length >= 6) {
            return pathParts[5];
        }
        if (pathParts.length > 0) {
            const projectPart = pathParts.find(part => part.match(/^P\d+/));
            if (projectPart) {
                return projectPart;
            }
            const nonGenericParts = pathParts.filter(part =>
                !part.includes('actividad_') &&
                !part.includes('tarea_') &&
                !part.includes('indicador_') &&
                !part.includes('medio_verificacion_') &&
                part !== 'DocumentosPOA'
            );
            if (nonGenericParts.length > 0) {
                return nonGenericParts[0];
            }
        }
        return 'No especificado';
    };

    const exportToExcel = async () => {
        if (!permissionExport) {
            message.warning("No tiene permisos para exportar");
            return;
        }

        if (!filteredFiles || filteredFiles.length === 0) {
            message.warning("No hay datos para exportar");
            return;
        }

        setExporting(true);

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Informe Actas de Entrega");

            const titleStyle = {
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FF042956" },
                },
                font: {
                    bold: true,
                    color: { argb: "FFFFFFFF" },
                    size: 16,
                },
                border: {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                },
                alignment: { vertical: "middle", horizontal: "center" },
            };

            const headerStyle = {
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FF60B5EF" },
                },
                font: {
                    bold: true,
                    color: { argb: "FF000000" },
                    size: 12,
                },
                border: {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                },
                alignment: { vertical: "middle", horizontal: "center" },
            };

            const dataStyle = {
                border: {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                },
                alignment: { vertical: "middle", horizontal: "left" },
            };

            worksheet.mergeCells("A1:G1");
            const titleCell = worksheet.getCell("A1");
            titleCell.value = "Informe Actas De Entrega";
            titleCell.style = titleStyle;

            const headerRow = worksheet.getRow(2);
            headerRow.values = [
                "Archivo",
                "Proyecto",
                "Estado",
                "Código",
                "Subido por",
                "Validado por",
                "Fecha Validación",
            ];
            headerRow.eachCell((cell) => {
                cell.style = headerStyle;
            });
            worksheet.columns = [
                { key: "nameResource", width: 40 },
                { key: "proyecto", width: 30 },
                { key: "estado", width: 15 },
                { key: "codUnico", width: 20 },
                { key: "user", width: 20 },
                { key: "validado_user", width: 20 },
                { key: "fecha_validacion", width: 20 },
            ];
            const getStatusText = (file) => {
                if (file.validado === 2) return "Validado";
                if (file.validado === 1) return "Sin Validar";
                return "Sin Registrar";
            };
            filteredFiles.forEach((file, index) => {
                const row = worksheet.getRow(index + 3);
                row.values = [
                    file.nameResource || "-",
                    getProjectName(file.fullPath) || "-",
                    getStatusText(file),
                    file.codUnico || "-",
                    file.user || "-",
                    file.validado_user || "-",
                    file.fecha_validacion
                        ? new Date(file.fecha_validacion).toLocaleDateString()
                        : "-",
                ];
                row.eachCell((cell) => {
                    cell.style = dataStyle;
                });
            });
            worksheet.columns.forEach((column) => {
                let maxLength = 0;
                column.eachCell({ includeEmpty: true }, (cell) => {
                    const columnLength = cell.value ? cell.value.toString().length : 10;
                    if (columnLength > maxLength) {
                        maxLength = columnLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 50);
            });
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Informe_Actas_Entrega_${new Date()
                .toISOString()
                .split("T")[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            message.success(
                `Se exportaron ${filteredFiles.length} registros correctamente`
            );
        } catch (error) {
            console.error("Error al exportar Excel:", error);
            message.error("Error al exportar el archivo Excel");
        } finally {
            setExporting(false);
        }
    };

    return (
        <Tooltip
            title={
                !permissionExport ? "No tiene permisos para exportar" : "Exportar a Excel"
            }
        >
            <Button
                type="primary"
                icon={
                    <DownloadOutlined
                        style={{
                            color: "#ffffff",
                            fontSize: "18px",
                        }}
                    />
                }
                onClick={exportToExcel}
                loading={exporting}
                disabled={
                    disabled ||
                    exporting ||
                    !permissionExport ||
                    !filteredFiles ||
                    filteredFiles.length === 0
                }
                style={{
                    backgroundColor: "#4ea323ff",
                    borderColor: "#4ea323ff",
                    height: "35px",
                    fontWeight: "bold",
                }}
            >
                {exporting ? "Exportando..." : "Exportar Excel"}
            </Button>
        </Tooltip>
    );
};